import { replayCase } from '../lib';

import type {
  MapSnapshotAircraftFix,
  MapSnapshotCaseBuffer,
  PlaybackPackWire,
  WeatherKeyframe,
} from '../api';
import type { HistoricalCase } from '../lib';

export const PACK_WINDOW_MS = 48 * 60 * 60 * 1000;

export interface TrackSample {
  tMs: number;
  latitude: number;
  longitude: number;
  heading: number;
}

export interface PlaybackPack {
  fromMs: number;
  toMs: number;
  cases: ReadonlyMap<string, MapSnapshotCaseBuffer>;
  keyframes: readonly WeatherKeyframe[];
  keyframeMs: readonly number[];
  tracks: ReadonlyMap<number, readonly TrackSample[]>;
}

export interface PackReplay {
  cases: HistoricalCase[];
  aircraftFixes: MapSnapshotAircraftFix[];
  weather: WeatherKeyframe | null;
}

/** Index of the last entry `<= tMs` in an ascending list, or -1. */
const lastIndexAtOrBefore = <T>(
  ascending: readonly T[],
  timeOf: (item: T) => number,
  tMs: number,
): number => {
  let lo = 0;
  let hi = ascending.length - 1;
  let found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (timeOf(ascending[mid]) <= tMs) {
      found = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return found;
};

const lastAtOrBefore = (ascending: readonly number[], tMs: number): number =>
  lastIndexAtOrBefore(ascending, ms => ms, tMs);

export const packFromWire = (wire: PlaybackPackWire): PlaybackPack => ({
  fromMs: Date.parse(wire.window.from),
  toMs: Date.parse(wire.window.to),
  cases: new Map(wire.cases.map(buffer => [buffer.caseId, buffer])),
  keyframes: wire.weather,
  keyframeMs: wire.weather.map(keyframe => Date.parse(keyframe.keyframeAt)),
  tracks: new Map(
    wire.aircraft.map(track => [
      track.aircraftId,
      track.fixes.map(fix => ({
        tMs: Date.parse(fix.locationTimestamp),
        latitude: fix.latitude,
        longitude: fix.longitude,
        heading: fix.heading,
      })),
    ]),
  ),
});

const mergeBuffer = (
  base: MapSnapshotCaseBuffer,
  incoming: MapSnapshotCaseBuffer,
): MapSnapshotCaseBuffer => {
  const seen = new Set(base.events.map(e => e.eventId));
  const added = incoming.events.filter(e => !seen.has(e.eventId));
  if (added.length === 0) {
    return base;
  }
  // Stable: an opener that shares an instant with a later event stays first.
  const events = [...base.events, ...added].sort(
    (a, b) => a.observedAtMs - b.observedAtMs,
  );
  return { ...base, caseLabel: incoming.caseLabel ?? base.caseLabel, events };
};

const keepsCase = (buffer: MapSnapshotCaseBuffer, floorMs: number): boolean =>
  buffer.events.some(e => e.observedAtMs >= floorMs) ||
  replayCase(buffer, floorMs) !== null;

/** Drops entries older than the floor, keeping the newest one before it. */
const trimAscending = <T>(
  items: readonly T[],
  timeOf: (item: T) => number,
  floorMs: number,
): T[] => {
  const firstKept = Math.max(0, lastAtOrBefore(items.map(timeOf), floorMs));
  return items.slice(firstKept);
};

export const mergePack = (
  base: PlaybackPack | null,
  incoming: PlaybackPack,
  nowMs: number,
): PlaybackPack => {
  const floorMs = nowMs - PACK_WINDOW_MS;

  const cases = new Map(base?.cases ?? []);
  incoming.cases.forEach((buffer, caseId) => {
    const existing = cases.get(caseId);
    cases.set(caseId, existing ? mergeBuffer(existing, buffer) : buffer);
  });
  cases.forEach((buffer, caseId) => {
    if (!keepsCase(buffer, floorMs)) {
      cases.delete(caseId);
    }
  });

  const baseKeyframes = base?.keyframes ?? [];
  const lastBaseMs = base?.keyframeMs.at(-1) ?? -Infinity;
  const keyframes = trimAscending(
    [
      ...baseKeyframes,
      ...incoming.keyframes.filter(
        (_, i) => incoming.keyframeMs[i] > lastBaseMs,
      ),
    ],
    keyframe => Date.parse(keyframe.keyframeAt),
    floorMs,
  );

  const tracks = new Map(base?.tracks ?? []);
  incoming.tracks.forEach((samples, aircraftId) => {
    const existing = tracks.get(aircraftId) ?? [];
    const lastMs = existing.at(-1)?.tMs ?? -Infinity;
    tracks.set(aircraftId, [
      ...existing,
      ...samples.filter(s => s.tMs > lastMs),
    ]);
  });
  tracks.forEach((samples, aircraftId) => {
    const trimmed = trimAscending(samples, s => s.tMs, floorMs);
    if (trimmed.length === 0 || trimmed[trimmed.length - 1].tMs < floorMs) {
      tracks.delete(aircraftId);
    } else {
      tracks.set(aircraftId, trimmed);
    }
  });

  return {
    fromMs: Math.max(
      Math.min(base?.fromMs ?? incoming.fromMs, incoming.fromMs),
      floorMs,
    ),
    toMs: Math.max(base?.toMs ?? incoming.toMs, incoming.toMs),
    cases,
    keyframes,
    keyframeMs: keyframes.map(keyframe => Date.parse(keyframe.keyframeAt)),
    tracks,
  };
};

const lerpHeading = (from: number, to: number, f: number): number => {
  const delta = ((to - from + 540) % 360) - 180;
  return (from + delta * f + 360) % 360;
};

export const interpolateTrack = (
  samples: readonly TrackSample[],
  tMs: number,
): TrackSample | null => {
  const i = lastIndexAtOrBefore(samples, s => s.tMs, tMs);
  if (i < 0) {
    return null;
  }
  const a = samples[i];
  if (a.tMs === tMs) {
    return a;
  }
  const b = samples[i + 1];
  if (!b) {
    return null;
  }
  const f = (tMs - a.tMs) / (b.tMs - a.tMs);
  return {
    tMs,
    latitude: a.latitude + (b.latitude - a.latitude) * f,
    longitude: a.longitude + (b.longitude - a.longitude) * f,
    heading: lerpHeading(a.heading, b.heading, f),
  };
};

export const replayPackAt = (pack: PlaybackPack, tMs: number): PackReplay => {
  const cases: HistoricalCase[] = [];
  pack.cases.forEach(buffer => {
    const replayed = replayCase(buffer, tMs);
    if (replayed) {
      cases.push(replayed);
    }
  });

  const aircraftFixes: MapSnapshotAircraftFix[] = [];
  const locationTimestamp = new Date(tMs).toISOString();
  pack.tracks.forEach((samples, aircraftId) => {
    const last = samples[samples.length - 1];
    const at =
      last && tMs > last.tMs && tMs <= pack.toMs
        ? last
        : interpolateTrack(samples, tMs);
    if (at) {
      aircraftFixes.push({
        aircraftId,
        locationTimestamp,
        latitude: at.latitude,
        longitude: at.longitude,
        heading: at.heading,
      });
    }
  });

  const k = lastAtOrBefore(pack.keyframeMs, tMs);
  return { cases, aircraftFixes, weather: k < 0 ? null : pack.keyframes[k] };
};
